import { useMutation, useQueryClient } from "@tanstack/react-query";
import { configuration } from "../../../configuration";
import { useSignalR } from "../../../hooks";
import { EditSectionDto, FetchError } from "../../types";
import { useAccessToken } from "../../use-access-token";
import { queryKeys } from "../queryKeys";

type Variables = {
  planGuid: string;
  guid: string;
  blockGuid: string;
  name: string;
  hintText: string;
  sortOrder: string;
  modifiedByUserId: string;
};

const useEditSectionRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function editSectionRequest(variables: Variables): Promise<void> {
    const accessToken = await getAccessToken();
    const body: EditSectionDto = {
      blockGuid: variables.blockGuid,
      name: variables.name,
      hintText: variables.hintText,
      sortOrder: variables.sortOrder,
      modifiedByUserId: variables.modifiedByUserId,
    };

    const response = await fetch(`${configuration.apiRootUrl}/sections/${variables.guid}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "put",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { editSectionRequest };
};

const useEditSection = () => {
  const { editSectionRequest } = useEditSectionRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  return useMutation({
    mutationFn: editSectionRequest,
    onSuccess: (_data, variables) => {
      signalR.sendRefetchPlan(variables.planGuid);
    },
    onSettled: (_data, _error, variables, _context) => {
      queryClient.invalidateQueries(queryKeys.plans.getPlan(variables.planGuid));
      queryClient.invalidateQueries(queryKeys.plans.getStructure(variables.planGuid));
      queryClient.invalidateQueries(queryKeys.plans.getPlanSubitems(variables.planGuid));
    },
  });
};

export { useEditSection };
